import { ThemeEvents } from '@theme/events';
import { getPreferencesConsent, onPreferencesConsentChange, grantPreferencesConsent } from '@theme/cookie-consent';

/**
 * Shared glue around the Swym Wishlist Plus storefront SDK (`_swat`), loaded
 * page-wide by the app embed. Everything resolves through SwymCallbacks, so
 * nothing here runs (and buttons stay hidden) until the SDK is actually up.
 *
 * Swym is also blocked outright until the visitor grants the Preferences cookie
 * category, and that promise then never settles at all. That case is singled out
 * from every other missing SDK (VIS-956) and reads its signal from Cookiebot
 * through @theme/cookie-consent, because it is the only one the visitor can fix.
 */

/** @type {Promise<Object> | null} */
let swatPromise = null;

/**
 * Resolves with the Swym SDK instance once it has loaded.
 * @returns {Promise<Object>}
 */
export function getSwat() {
  if (!swatPromise) {
    swatPromise = new Promise((resolve) => {
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(resolve);
    });
  }
  return swatPromise;
}

/** @type {Promise<Object[]> | null} */
let listsPromise = null;

/**
 * Fetches the visitor's wishlist lists, cached across all callers.
 * @param {Object} swat - The Swym SDK instance.
 * @returns {Promise<Object[]>}
 */
export function fetchLists(swat) {
  if (!listsPromise) {
    listsPromise = new Promise((resolve, reject) => {
      swat.fetchLists({
        callbackFn: (lists) => resolve(lists || []),
        errorFn: reject,
      });
    });
  }
  return listsPromise;
}

export function invalidateLists() {
  listsPromise = null;
}

/**
 * Returns the visitor's default wishlist, creating it on first use. This is the
 * right target for adding only: deletes go through deleteFromLists(), which
 * addresses whichever list actually holds the item (VIS-963).
 * @param {Object} swat - The Swym SDK instance.
 * @returns {Promise<Object>}
 */
export async function ensureList(swat) {
  const lists = await fetchLists(swat);
  if (lists.length > 0) return lists[0];

  // NB: unlike fetchLists, createList takes positional callbacks — the
  // documented { callbackFn, errorFn } object form never invokes them.
  const created = await new Promise((resolve, reject) => {
    swat.createList({ lname: 'Wishlist' }, resolve, reject);
  });
  listsPromise = Promise.resolve([created]);
  return created;
}

/**
 * The saved entries for a product, each paired with the list holding it.
 * @param {Object[]} lists
 * @param {number} empi - Swym product id.
 * @param {number | null} epi - Swym variant id, or null for every variant of the product.
 * @returns {{ list: Object, item: Object }[]}
 */
function entriesFor(lists, empi, epi) {
  const entries = [];

  for (const list of lists) {
    for (const item of list.listcontents || []) {
      if (Number(item.empi) !== empi) continue;
      if (epi != null && Number(item.epi) !== epi) continue;
      entries.push({ list, item });
    }
  }

  return entries;
}

/**
 * Whether a product variant is present in any of the given lists.
 * @param {Object[]} lists
 * @param {{ empi: number, epi: number }} payload - Swym ids (empi = product, epi = variant).
 * @returns {boolean}
 */
export function isInLists(lists, { empi, epi }) {
  return entriesFor(lists, empi, epi).length > 0;
}

/**
 * Removes a product variant from every list that holds it.
 *
 * Deleting against ensureList() instead looks like it works and does nothing:
 * Swym accepts a delete addressed to a list that does not hold the item,
 * reports success and even fires removedFromWishlist, so the card is dropped
 * optimistically and comes back on the next render. Any visitor whose account
 * holds more than one list hits this for everything outside the first list
 * (VIS-963).
 *
 * Every holder is cleared rather than just the first, because membership is
 * read across all lists: a copy left behind keeps the heart filled and puts the
 * card back.
 *
 * Each delete is addressed with the ids Swym has on record, not the ones the
 * caller asked with, and a variant that matches nothing falls back to every
 * saved variant of the product. The two differ once a saved variant stops
 * resolving: the card then renders the product's first available variant, so
 * the payload carries a variant that was never saved, an exact delete is
 * another silent no-op, and the card comes back for good.
 *
 * @param {Object} swat - The Swym SDK instance.
 * @param {{ empi: number, epi: number, du?: string }} payload
 */
export async function deleteFromLists(swat, { empi, epi, du }) {
  const lists = await fetchLists(swat);
  let entries = entriesFor(lists, empi, epi);

  if (entries.length === 0) {
    entries = entriesFor(lists, empi, null);

    if (entries.length > 0) {
      console.warn(
        `Wishlist: product ${empi} is not saved under variant ${epi}, which usually means that ` +
          'variant no longer exists; removing every saved variant of it instead.'
      );
    } else {
      console.warn(
        `Wishlist: product ${empi} is in no list, so there was nothing to remove. It was most ` +
          'likely removed from another tab or from Swym itself.'
      );
    }
  }

  for (const { list, item } of entries) {
    const stored = { empi: Number(item.empi), epi: Number(item.epi), du: item.du ?? du };

    await new Promise((resolve, reject) => {
      swat.deleteFromList(list.lid, stored, resolve, reject);
    });
  }
}

const changeListeners = new Set();

/**
 * Subscribes to wishlist content changes (from any tab component or Swym UI).
 * @param {Function} listener
 * @returns {Function} Unsubscribe.
 */
export function onWishlistChange(listener) {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

export function notifyWishlistChange() {
  invalidateLists();
  for (const listener of changeListeners) listener();
}

/**
 * Fills the header heart (and its drawer twin) while the wishlist holds
 * something, per the dev note on Figma 11954-66797: "il cuore è riempito quando
 * ci sono dei prodotti nei preferiti". The fill is the only indicator by
 * design, there is no count badge, so this is a boolean and not a number.
 *
 * Runs on every change and once the SDK reports in, so the heart starts as a
 * plain outline and fills in after Swym loads. That late fill-in is accepted
 * (VIS-707): the alternative is blocking first paint on a third-party SDK.
 */
async function reflectWishlistFilled(swat) {
  let filled = false;

  try {
    const lists = await fetchLists(swat);
    filled = lists.some((list) => (list.listcontents || []).length > 0);
  } catch {
    // Leave the heart as it is; the next change event retries.
    return;
  }

  for (const link of document.querySelectorAll('[data-wishlist-nav]')) {
    link.classList.toggle('wishlist-nav--filled', filled);
  }
}

/**
 * Briefly pulses the wishlist entry point in the header as feedback when an
 * item is added, mirroring the favorite-store nudge ring. Purely decorative,
 * and the animation itself is reduced-motion-gated in CSS.
 */
function pulseWishlistNav() {
  for (const link of document.querySelectorAll('[data-wishlist-nav]')) {
    link.classList.remove('wishlist-nav-pulse');
    // Force a reflow so re-adding the class restarts a mid-flight animation.
    void link.offsetWidth;
    link.classList.add('wishlist-nav-pulse');
    link.addEventListener('animationend', () => link.classList.remove('wishlist-nav-pulse'), {
      once: true,
    });
  }
}

// Keep every component in sync when Swym reports a change, whichever surface
// (our buttons, the wishlist page, Swym's own UI) triggered it. Adds also pulse
// the header entry point; removals just re-sync. The filled heart follows every
// change, plus once on load for a list that was already non-empty.
getSwat().then((swat) => {
  reflectWishlistFilled(swat);
  onWishlistChange(() => reflectWishlistFilled(swat));

  const events = swat.JSEvents;
  if (!swat.evtLayer || !events) return;

  if (events.addedToWishlist) {
    swat.evtLayer.addEventListener(events.addedToWishlist, () => {
      notifyWishlistChange();
      pulseWishlistNav();
    });
  }

  if (events.removedFromWishlist) {
    swat.evtLayer.addEventListener(events.removedFromWishlist, () => notifyWishlistChange());
  }
});

/** How long to give Swym to come up on its own once consent has been granted. */
const CONSENT_GRANT_SDK_MS = 3000;

/**
 * Opens the shared consent explainer (snippets/wishlist-consent-dialog.liquid,
 * rendered once from layout/theme.liquid). A no-op when it isn't on the page,
 * e.g. with settings.wishlist_enabled off, where no toggle renders either.
 */
function showWishlistConsentDialog() {
  /** @type {any} */ (document.getElementById('wishlist-consent-dialog'))?.showDialog?.();
}

// The dialog's CTA. Delegated on document rather than bound to the element, so
// it doesn't depend on dialog.js having upgraded the custom element by the time
// this module runs, and scoped by id so the wishlist page's own consent CTA
// (sections/wishlist.liquid, handled in wishlist-page.js) isn't caught twice.
//
// Close first, then grant: the visible result of the click is the page coming
// alive (or, worst case, reloading via the watcher below), and the dialog must
// be out of the way when it does. closeDialog() only calls dialog.close() and
// releases the body's `position: fixed` after awaiting its close animation, so
// awaiting it keeps that teardown ahead of whatever the grant sets in motion.
// The snippet suppresses the close animation, which is what keeps this from
// being a visible wait (VIS-956); the grant itself never opens the banner
// unless the fallback inside grantPreferencesConsent() has to (VIS-976).
document.addEventListener('click', async (event) => {
  const { target } = event;
  if (!(target instanceof Element)) return;
  if (!target.closest('#wishlist-consent-dialog [data-wishlist-consent-grant]')) return;

  await /** @type {any} */ (document.getElementById('wishlist-consent-dialog'))?.closeDialog?.();
  grantPreferencesConsent();
});

// Bringing the wishlist to life after consent is granted, once for the page and
// not once per toggle: a 48-card grid must not schedule 48 reloads.
//
// Cookiebot unblocks scripts it held back by rewriting them, so Swym coming
// alive in place is the happy path and every pending getSwat() below resolves
// on its own. A script blocked before parse generally needs a fresh document
// though, so reload if the SDK hasn't reported in shortly after. The visitor
// has just acted on the banner, so a reload reads as the result of their click.
//
// Strictly for a visitor who was blocked, for two separate reasons.
// CookiebotOnConsentReady fires on every ordinary page load and not only on an
// answer, so reacting to the state alone would reload in a loop wherever
// consent is granted but Swym is legitimately absent (app embed off). And a
// first answer (pending to granted) must not reload either: this module loads
// on every page, so that would reload the page under every visitor who accepts
// the banner anywhere on the site, including those who never went near a
// wishlist. Only someone who was shown the blocked state and acted on it has
// asked for this.
//
// Sticky rather than a previous/current comparison, because a comparison only
// holds if the grant is the very next event. The CTA's submitCustomConsent()
// emits CookiebotOnAccept directly, but its renew() fallback reopens the banner
// and may clear hasResponse and emit while it is open, so the accept would read
// as pending to granted and a comparison would drop it. That they were blocked
// at all is true whichever path the grant takes.
let blockedSeen = getPreferencesConsent() === 'blocked';

onPreferencesConsentChange(async (state) => {
  if (state === 'blocked') {
    blockedSeen = true;
    return;
  }

  if (state !== 'granted' || !blockedSeen) return;

  // Consumed: a later grant with no decline in between is an ordinary page
  // event, not someone acting on the blocked state.
  blockedSeen = false;

  const alive = await Promise.race([
    getSwat().then(() => true),
    new Promise((resolve) => setTimeout(() => resolve(false), CONSENT_GRANT_SDK_MS)),
  ]);

  if (!alive) window.location.reload();
});

/**
 * The wishlist toggle rendered by snippets/wishlist-button.liquid, on the PDP
 * and on every product tile. Hidden and disabled until the Swym SDK is
 * available, with one exception: when Swym is blocked because the visitor
 * declined the Preferences cookie category, the toggle shows in a blocked state
 * that explains itself on click (VIS-956). Every other reason for an absent SDK
 * stays invisible, because the visitor cannot clear it (VIS-910).
 */
class WishlistButton extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Function | null} */
  #unsubscribe = null;
  /** @type {Function | null} */
  #unsubscribeConsent = null;
  /** @type {Object | null} */
  #swat = null;
  #busy = false;
  #blocked = false;
  /** @type {{ hidden: boolean, disabled: boolean, label: string | null } | null} */
  #blockedAttributes = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#button?.addEventListener('click', () => this.#handleClick(), { signal });

    // Listen on the enclosing card when there is one, not on document: a card's
    // variant update never reaches document, because ProductCard stops its
    // propagation so card-scoped changes can't leak into the page
    // (product-card.js, #handleVariantUpdate). stopPropagation still lets other
    // listeners on the card itself run, which is the same hook
    // variant-scoped-block uses. Without this the tile keeps the first variant
    // and saves the wrong colour (VIS-910, Carla's QA). On the PDP there is no
    // card ancestor, so this stays the document listener it always was.
    const variantScope = this.closest('product-card') ?? document;

    variantScope.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        const { resource, data } = event.detail || {};
        if (!resource || String(data?.productId) !== this.dataset.productId) return;
        this.dataset.variantId = String(resource.id);
        this.#refresh();
      },
      { signal }
    );

    this.#unsubscribe = onWishlistChange(() => this.#refresh());
    this.#unsubscribeConsent = onPreferencesConsentChange(() => this.#reflectConsent());
    this.#init();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#unsubscribe?.();
    this.#unsubscribeConsent?.();
  }

  get #button() {
    return this.querySelector('button');
  }

  get #payload() {
    return {
      empi: Number(this.dataset.productId),
      epi: Number(this.dataset.variantId),
      du: this.dataset.productUrl,
    };
  }

  async #init() {
    // Consent first, because getSwat() never settles while Swym is blocked:
    // anything behind that await would never run in exactly the case this state
    // exists for. The await below still stands, so a grant that does bring Swym
    // up in place reveals a working toggle without waiting for a reload.
    this.#reflectConsent();

    const swat = await getSwat();
    if (!this.isConnected) return;

    // Swym's remote kill-switch (Swym Admin, not our theme setting): when off,
    // reads still work but addToList/deleteFromList throw "Wishlist app is not
    // enabled" — keep the button hidden rather than showing a dead toggle.
    // Only an explicit false disables, so a missing config can't hide the
    // feature.
    if (swat.retailerSettings?.Wishlist?.Enabled === false) {
      console.warn('Wishlist is disabled in the Swym remote config; hiding the wishlist button.');
      return;
    }

    this.#swat = swat;
    await this.#reveal();
  }

  /**
   * Applies the current consent state, on mount and on every Cookiebot event.
   * Only the blocked case is visible: a missing CMP and an unanswered banner
   * both keep the toggle hidden, the first because nothing the visitor does
   * would clear it, the second because the answer is one click away and a
   * blocked heart would flash and then resolve itself.
   */
  #reflectConsent() {
    // A live SDK wins: once the toggle works, consent events are noise.
    if (this.#swat) return;

    if (getPreferencesConsent() === 'blocked') {
      this.#block();
      return;
    }

    // Granted, still pending, or no CMP at all. The pending getSwat() in #init
    // reveals the toggle if Swym comes up and the module-level watcher reloads
    // if it doesn't, so there is nothing to render here yet. Putting the toggle
    // back the way it shipped is the point though: dropping the flag alone
    // would leave a greyed heart on screen that no longer even opens the
    // dialog, which is the dead end this whole state exists to avoid.
    this.#unblock();
  }

  /**
   * The consent-blocked state: visible, focusable and clickable, but not a
   * working toggle. `aria-disabled` rather than `disabled`, because a disabled
   * button cannot receive the click that opens the explainer, which is the
   * whole point of showing it.
   */
  #block() {
    const button = this.#button;
    if (!button) return;

    // Only this method knows which attributes it actually cleared, and the set
    // is not the same everywhere: in the theme editor the host ships without
    // `hidden` at all, so a blanket restore later would hide a toggle the
    // editor expects to see. Captured once per entry into the state, or a
    // second consent event while still blocked would snapshot our own work.
    if (!this.#blocked) {
      this.#blockedAttributes = {
        hidden: this.hasAttribute('hidden'),
        disabled: button.hasAttribute('disabled'),
        label: button.getAttribute('aria-label'),
      };
    }

    this.#blocked = true;
    button.removeAttribute('disabled');
    button.setAttribute('aria-disabled', 'true');
    if (this.dataset.labelBlocked) button.setAttribute('aria-label', this.dataset.labelBlocked);
    // aria-pressed stays false: nothing is saved, and nothing can be.
    this.removeAttribute('hidden');
  }

  /**
   * Puts back exactly what #block() cleared, for a toggle that leaves the
   * blocked state without the SDK arriving: a renewed banner going back to
   * unanswered, or a grant that doesn't bring Swym up in place. #reveal() is
   * the other way out of the state and deliberately doesn't come through here,
   * since it drops those same attributes on purpose.
   */
  #unblock() {
    const previous = this.#blockedAttributes;
    this.#blocked = false;
    this.#blockedAttributes = null;
    if (!previous) return;

    const button = this.#button;
    button?.removeAttribute('aria-disabled');

    if (previous.label === null) button?.removeAttribute('aria-label');
    else button?.setAttribute('aria-label', previous.label);

    if (previous.disabled) button?.setAttribute('disabled', '');
    if (previous.hidden) this.setAttribute('hidden', '');
  }

  #handleClick() {
    if (this.#blocked) {
      showWishlistConsentDialog();
      return;
    }
    this.#toggle();
  }

  /**
   * Brings the toggle live: current saved state, enabled, visible. Split out of
   * #init because a morph can undo all three, see restoreAfterMorph(). Clears
   * the blocked state first, so a toggle that comes alive after a late grant
   * never keeps a stale aria-disabled.
   */
  async #reveal() {
    this.#blocked = false;
    this.#blockedAttributes = null;
    this.#button?.removeAttribute('aria-disabled');
    await this.#refresh();
    this.#button?.removeAttribute('disabled');
    this.removeAttribute('hidden');
  }

  /**
   * Re-applies what the SDK already knows after the element's attributes have
   * been reset by a morph. Public because the module-level observer calls it.
   * Either reset attribute is a trigger: in the theme editor the host renders
   * without `hidden` at all, so there the button's `disabled` is the only sign
   * a morph has been through.
   *
   * The blocked state has to survive a morph too, or a PLP filter, pagination
   * or a predictive-search keystroke would silently take every blocked heart
   * back to hidden and leave the visitor with the dead end this state exists to
   * explain.
   */
  restoreAfterMorph() {
    if (!this.hasAttribute('hidden') && !this.#button?.hasAttribute('disabled')) return;

    if (this.#swat) {
      this.#reveal();
    } else if (this.#blocked) {
      this.#block();
    }
  }

  async #refresh() {
    if (!this.#swat) return;

    try {
      const lists = await fetchLists(this.#swat);
      this.#setSaved(isInLists(lists, this.#payload));
    } catch {
      // Keep the current state; the next change event retries.
    }
  }

  /** @param {boolean} saved */
  #setSaved(saved) {
    const button = this.#button;
    if (!button) return;
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', saved ? this.dataset.labelRemove : this.dataset.labelAdd);
  }

  async #toggle() {
    if (this.#busy || !this.#swat) return;
    this.#busy = true;

    try {
      const saved = this.#button?.getAttribute('aria-pressed') === 'true';

      if (saved) {
        await deleteFromLists(this.#swat, this.#payload);
      } else {
        const list = await ensureList(this.#swat);
        await new Promise((resolve, reject) => {
          this.#swat.addToList(list.lid, this.#payload, resolve, reject);
        });
      }

      this.#setSaved(!saved);
      notifyWishlistChange();
    } catch (error) {
      console.warn('Wishlist toggle failed:', error);
      this.#refresh();
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

// Morph re-applies the server markup's attributes to nodes it reuses, keeping
// only the small allowlist in morph.js, so a toggle that had been revealed comes
// back hidden, disabled and unsaved after anything that morphs a card: a
// predictive-search keystroke, a PLP filter, pagination. The element itself is
// reused, so connectedCallback never runs again — watch for those attributes
// reappearing instead and re-apply what the SDK already knows. One observer for
// the whole document: cheaper than one per toggle in a 48-card grid, and it also
// covers toggles that mount later. Our own removeAttribute calls re-enter this
// callback once and stop there, since restoreAfterMorph only acts on a toggle
// that is hidden or disabled.
//
// Both attributes are watched, on the host and on its button, because the host
// ships `hidden` only outside the theme editor: in the editor a morph would
// otherwise leave the button disabled until reload.
if (typeof MutationObserver !== 'undefined') {
  new MutationObserver((records) => {
    for (const { target } of records) {
      if (!(target instanceof HTMLElement)) continue;
      const host = target.localName === 'wishlist-button' ? target : target.closest('wishlist-button');
      /** @type {any} */ (host)?.restoreAfterMorph?.();
    }
  }).observe(document.documentElement, { attributeFilter: ['hidden', 'disabled'], subtree: true });
}
